import { forwardRef, useEffect, useRef, useState } from 'react';
import Lottie from 'lottie-react';
import { fetchLottieData } from '../../utils/lazyLottieData';
import { usePausedWhenHidden } from '../../hooks/usePausedWhenHidden';
import { prefersReducedMotion } from '../../utils/devicePerformance';

/**
 * Loads Lottie JSON only when near the viewport (or `eager` on hero).
 * Avoids bundling multi‑MB animation files into the initial Services chunk.
 */
const LazyLottie = forwardRef(function LazyLottie(
  {
    importFn,
    cacheKey,
    className,
    loop = true,
    autoplay = true,
    eager = false,
    loadWhen = false,
    rootMargin = '160px',
    placeholderMinHeight = 120,
    pauseWhenHidden = true,
    ...lottieProps
  },
  lottieRef
) {
  const containerRef = useRef(null);
  const [inView, setInView] = useState(eager);
  const [data, setData] = useState(null);
  const shouldLoad = eager || loadWhen || inView;
  const { paused: hiddenPaused } = usePausedWhenHidden(containerRef, {
    rootMargin,
    enabled: pauseWhenHidden && Boolean(data),
  });
  const reduceMotion = prefersReducedMotion();

  useEffect(() => {
    if (eager || loadWhen || inView) return undefined;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0.01 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [eager, loadWhen, inView, rootMargin]);

  useEffect(() => {
    if (!shouldLoad) return undefined;
    let cancelled = false;
    fetchLottieData(importFn, cacheKey).then((json) => {
      if (!cancelled) setData(json);
    });
    return () => {
      cancelled = true;
    };
  }, [shouldLoad, importFn, cacheKey]);

  useEffect(() => {
    if (!data) return undefined;
    const anim = lottieRef?.current;
    if (!anim) return undefined;
    if (hiddenPaused || reduceMotion) {
      anim.pause?.();
      return undefined;
    }
    if (autoplay) anim.play?.();
    return undefined;
  }, [data, autoplay, hiddenPaused, reduceMotion, lottieRef]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={data ? undefined : { minHeight: placeholderMinHeight }}
      aria-hidden={!data}
    >
      {data ? (
        <Lottie
          lottieRef={lottieRef}
          animationData={data}
          loop={loop}
          autoplay={autoplay && !reduceMotion && !hiddenPaused}
          rendererSettings={{ progressiveLoad: true, hideOnTransparent: true }}
          {...lottieProps}
        />
      ) : null}
    </div>
  );
});

export default LazyLottie;
